import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudRain, Droplets, TrendingUp, Wind,
  Leaf, Wallet, Landmark, X, Thermometer,
} from 'lucide-react';
import Header from '../../components/Header';
import WeatherWidget from '../../components/WeatherWidget';
import Footer from '../../components/Footer';
import NotificationPopup from '../Notificationpopup';
import { fetchActiveCrops, fetchCurrentWeather, detectCity } from '../../services/farmerApi';

/* ------------- Mandi API helper ------------- */
const MANDI_API = 'https://api.data.gov.in/resource/9ef84268-d588-465a-a308-a864a43d0070?api-key=579b464db66ec23bdd0000014fa11c3e7fb045f265941a3d91e9399d&format=json&limit=5000';

const SCHEMES = [
  { name: 'PM-KISAN', eligible: true, note: 'Next installment due Jun 2026' },
  { name: 'Pradhan Mantri Fasal Bima Yojana', eligible: true, note: 'Enrolment window: kharif 2026' },
  { name: 'Soil Health Card', eligible: true, note: 'Renew card – last issued 2023' },
  { name: 'Pradhan Mantri Kisan Maan Dhan Yojana', eligible: true, note: 'Renew card – last issued 2023' },
  { name: 'Pradhan Mantri Krishi Sinchayee Yojana (PMKSY)', eligible: true, note: 'Renew card - last issued 2021' }
];


/* ------------- Shared styles (layout helpers only) ------------- */
const grid = (min) => ({
  display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 16,
});
const sectionGap = { marginTop: 18 };

/* ------------- Hero ------------- */
const Hero = ({ name }) => (
  <div style={{
    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%)',
    color: '#1b5e20', padding: '24px 28px', borderRadius: 18,
    boxShadow: '0 12px 40px rgba(27,94,32,0.18)',
    marginBottom: 24,
    position: 'relative',
    overflow: 'hidden',
  }}>
    {/* Decorative blobs */}
    <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(27,94,32,0.06)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: -40, right: 120, width: 140, height: 140, borderRadius: '50%', background: 'rgba(27,94,32,0.06)', pointerEvents: 'none' }} />
    <div style={{ position: 'relative', zIndex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, opacity: 0.55, textTransform: 'uppercase' }}>Farmer Dashboard</div>
      <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6, letterSpacing: -0.5 }}>Namaste, {name || 'Kisan'} 🌾</div>
      <div style={{ opacity: 0.65, fontSize: 13, marginTop: 6, fontWeight: 500 }}>
        Your farm at a glance — weather, crop health, mandi prices and today's actions.
      </div>
    </div>
  </div>
);

/* ------------- Active Crops — dynamic from API ------------- */
const ActiveCrops = () => {
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchActiveCrops();
      setCrops(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const healthColor = (pct) => {
    if (pct >= 85) return '#28a745';
    if (pct >= 70) return '#f5c542';
    return '#e74c3c';
  };

  /* Skeleton row shown while loading */
  const SkeletonRow = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid rgba(46,125,50,0.1)' }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, background: '#c8e6c9', flexShrink: 0,
        animation: 'pulse 1.4s ease-in-out infinite'
      }} />
      <div style={{ flex: 1 }}>
        <div style={{
          height: 12, width: '60%', background: '#c8e6c9', borderRadius: 6, marginBottom: 6,
          animation: 'pulse 1.4s ease-in-out infinite'
        }} />
        <div style={{
          height: 10, width: '40%', background: '#c8e6c9', borderRadius: 6,
          animation: 'pulse 1.4s ease-in-out infinite'
        }} />
      </div>
      <div style={{
        width: 90, height: 8, background: '#c8e6c9', borderRadius: 999,
        animation: 'pulse 1.4s ease-in-out infinite'
      }} />
    </div>
  );

  const CropRow = ({ crop, last }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 0',
      borderBottom: last ? 'none' : '1px solid rgba(46,125,50,0.1)',
    }}>
      {/* Icon */}
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: `${crop.color}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontSize: 20,
      }}>
        {crop.icon || <Leaf size={20} color={crop.color} />}
      </div>

      {/* Name + field */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#1b5e20', fontSize: 14 }}>
          {crop.name}
          {crop.variety ? <span style={{ fontWeight: 500, color: '#5a8a5a' }}> – {crop.variety}</span> : null}
        </div>
        <div style={{ color: '#7aaa7a', fontSize: 12, marginTop: 2 }}>{crop.field}</div>
      </div>

      {/* Progress bar + % */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div style={{ width: 90, height: 8, background: 'rgba(46,125,50,0.15)', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{
            width: `${crop.health}%`, height: '100%',
            background: healthColor(crop.health), borderRadius: 999,
            transition: 'width 0.6s ease',
          }} />
        </div>
        <span style={{ fontWeight: 700, color: '#1b5e20', fontSize: 13, minWidth: 34 }}>
          {crop.health}%
        </span>
      </div>
    </div>
  );

  const top5 = crops.slice(0, 5);
  const season = crops[0]?.season || 'Kharif';

  return (
    <>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes cropSlide { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* ── Card ── */}
      <div style={{
        background: 'linear-gradient(160deg, #f1f8e9 0%, #dcedc8 55%, #c5e1a5 100%)',
        borderRadius: 18,
        boxShadow: '0 8px 32px rgba(46,125,50,0.12)',
        overflow: 'hidden',
        position: 'relative',
        minHeight: 280,
      }}>
        {/* Decorative blobs */}
        <div style={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, borderRadius: '50%', background: 'rgba(46,125,50,0.06)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(46,125,50,0.06)', pointerEvents: 'none' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(46,125,50,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
              🌾
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#2e7d32', letterSpacing: 0.3 }}>Active Crops</div>
              <div style={{ fontSize: 11, color: '#5a8a5a', marginTop: 1 }}>{season} Season</div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'rgba(46,125,50,0.12)', margin: '0 20px' }} />

        {/* Loading skeletons */}
        {loading && (
          <div style={{ padding: '12px 20px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < 3 ? '1px solid rgba(46,125,50,0.1)' : 'none' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#c8e6c9', flexShrink: 0, animation: 'pulse 1.4s ease-in-out infinite' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: 11, width: '55%', background: '#c8e6c9', borderRadius: 6, marginBottom: 6, animation: 'pulse 1.4s ease-in-out infinite' }} />
                  <div style={{ height: 9, width: '35%', background: '#c8e6c9', borderRadius: 6, animation: 'pulse 1.4s ease-in-out infinite' }} />
                </div>
                <div style={{ width: 80, height: 6, background: '#c8e6c9', borderRadius: 999, animation: 'pulse 1.4s ease-in-out infinite' }} />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{ padding: '28px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
            <div style={{ color: '#5a8a5a', fontSize: 13, marginBottom: 12 }}>{error}</div>
            <button onClick={load} style={{ background: '#2e7d32', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && crops.length === 0 && (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: '#5a8a5a', fontSize: 13 }}>
            🌱 No active crops found.
          </div>
        )}

        {/* Crop rows */}
        {!loading && !error && (
          <div style={{ padding: '8px 20px 0' }}>
            {top5.map((crop, i) => (
              <div key={crop.name} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 0',
                borderBottom: i < top5.length - 1 ? '1px solid rgba(46,125,50,0.1)' : 'none',
                animation: 'cropSlide 0.3s ease both',
                animationDelay: `${i * 0.07}s`,
              }}>
                {/* Icon bubble */}
                <div style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                  background: `${crop.color}30`,
                  border: `1.5px solid ${crop.color}60`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>
                  {crop.icon || '🌿'}
                </div>

                {/* Name + field */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#1b5e20', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {crop.name}
                    {crop.variety && <span style={{ fontWeight: 500, color: '#5a8a5a', fontSize: 12 }}> – {crop.variety}</span>}
                  </div>
                  <div style={{ color: '#7aaa7a', fontSize: 11, marginTop: 2 }}>{crop.field}</div>
                </div>

                {/* Progress + % */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <div style={{ width: 80, height: 5, background: 'rgba(46,125,50,0.15)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${crop.health}%`,
                      background: crop.health >= 85 ? '#4ade80' : crop.health >= 70 ? '#fbbf24' : '#f87171',
                      borderRadius: 999, transition: 'width 0.6s ease',
                      boxShadow: crop.health >= 85 ? '0 0 6px #4ade8088' : 'none',
                    }} />
                  </div>
                  <span style={{ fontWeight: 800, color: '#1b5e20', fontSize: 12, minWidth: 30 }}>{crop.health}%</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View All button — bottom */}
        {!loading && !error && crops.length > 5 && (
          <div style={{ padding: '12px 20px 16px' }}>
            <button
              onClick={() => setShowAll(true)}
              style={{
                width: '100%', padding: '10px 0',
                background: 'rgba(46,125,50,0.1)',
                border: '1.5px solid rgba(46,125,50,0.25)',
                borderRadius: 12, color: '#1b5e20', fontWeight: 700,
                fontSize: 13, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(46,125,50,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(46,125,50,0.1)'}
            >
              🌿 View All Crops ({crops.length})
            </button>
          </div>
        )}
      </div>

      {/* View All modal — light agri theme */}
      {showAll && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={() => setShowAll(false)}>
          <div style={{ background: '#f9fbe7', borderRadius: 18, padding: '24px', width: '100%', maxWidth: 520, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(46,125,50,0.18)', border: '1px solid rgba(46,125,50,0.15)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 800, color: '#1b5e20', fontSize: 17 }}>All Active Crops – {season}</div>
              <button onClick={() => setShowAll(false)} style={{ background: '#e8f5e9', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={16} color="#2e7d32" />
              </button>
            </div>
            {crops.map((crop, i) => (
              <div key={crop.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < crops.length - 1 ? '1px solid rgba(46,125,50,0.1)' : 'none' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${crop.color}30`, border: `1.5px solid ${crop.color}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{crop.icon || '🌿'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#1b5e20', fontSize: 13 }}>{crop.name}{crop.variety && <span style={{ color: '#5a8a5a', fontWeight: 500 }}> – {crop.variety}</span>}</div>
                  <div style={{ color: '#7aaa7a', fontSize: 11, marginTop: 2 }}>{crop.field}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <div style={{ width: 80, height: 5, background: 'rgba(46,125,50,0.15)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${crop.health}%`, background: crop.health >= 85 ? '#4ade80' : crop.health >= 70 ? '#fbbf24' : '#f87171', borderRadius: 999 }} />
                  </div>
                  <span style={{ fontWeight: 800, color: '#1b5e20', fontSize: 12, minWidth: 30 }}>{crop.health}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

/* ------------- Weather Detail Card — live seasonal data for detected location ------------- */
const WeatherDetailCard = ({ location }) => {
  const [active, setActive] = useState('preMonsoon');
  const [liveData, setLiveData] = useState(null);
  const [cityName, setCityName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadWeather = useCallback(async () => {
    if (!location) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCurrentWeather(location);
      if (data.error) throw new Error(data.error);
      setCityName(data.city || location);
      setLiveData(data.current);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    loadWeather();
    // Auto-refresh every 10 minutes
    const timer = setInterval(loadWeather, 10 * 60 * 1000);
    return () => clearInterval(timer);
  }, [loadWeather]);

  // Build seasonal climate data — use live current values to adjust the base averages
  // so the data reflects the actual location's climate character
  const tempOffset = liveData?.temp_c != null
    ? Math.round(liveData.temp_c) - 29   // 29°C is the India baseline for current month
    : 0;
  const humidOffset = liveData?.humidity != null
    ? Math.round(liveData.humidity) - 40
    : 0;

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Base India climatological averages adjusted by live offset
  const BASE = {
    1: { avgTemp: 15, minTemp: 8, maxTemp: 22, avgHumidity: 55, totalRain: 18, avgWind: 10, condition: 'Cool & dry' },
    2: { avgTemp: 18, minTemp: 11, maxTemp: 25, avgHumidity: 50, totalRain: 15, avgWind: 11, condition: 'Mild & dry' },
    3: { avgTemp: 24, minTemp: 16, maxTemp: 32, avgHumidity: 42, totalRain: 12, avgWind: 13, condition: 'Warm, pre-monsoon begins' },
    4: { avgTemp: 30, minTemp: 22, maxTemp: 38, avgHumidity: 38, totalRain: 8, avgWind: 15, condition: 'Hot & dry' },
    5: { avgTemp: 34, minTemp: 26, maxTemp: 42, avgHumidity: 40, totalRain: 14, avgWind: 17, condition: 'Very hot, thunderstorms' },
    6: { avgTemp: 32, minTemp: 26, maxTemp: 38, avgHumidity: 72, totalRain: 95, avgWind: 20, condition: 'Monsoon onset' },
    7: { avgTemp: 29, minTemp: 24, maxTemp: 34, avgHumidity: 85, totalRain: 210, avgWind: 18, condition: 'Peak monsoon' },
    8: { avgTemp: 28, minTemp: 24, maxTemp: 33, avgHumidity: 88, totalRain: 195, avgWind: 16, condition: 'Heavy rains' },
    9: { avgTemp: 28, minTemp: 23, maxTemp: 33, avgHumidity: 82, totalRain: 140, avgWind: 14, condition: 'Retreating monsoon' },
    10: { avgTemp: 26, minTemp: 19, maxTemp: 33, avgHumidity: 65, totalRain: 35, avgWind: 11, condition: 'Post-monsoon, pleasant' },
    11: { avgTemp: 20, minTemp: 13, maxTemp: 27, avgHumidity: 58, totalRain: 12, avgWind: 9, condition: 'Cool & clear' },
    12: { avgTemp: 15, minTemp: 8, maxTemp: 22, avgHumidity: 56, totalRain: 10, avgWind: 9, condition: 'Cold & dry' },
  };

  // Apply live offset to a month's data
  const adjust = (m) => ({
    ...BASE[m],
    avgTemp: BASE[m].avgTemp + tempOffset,
    minTemp: BASE[m].minTemp + tempOffset,
    maxTemp: BASE[m].maxTemp + tempOffset,
    avgHumidity: Math.min(100, Math.max(0, BASE[m].avgHumidity + humidOffset)),
    avgWind: liveData?.wind_kph != null
      ? Math.round(liveData.wind_kph * 0.8 + BASE[m].avgWind * 0.2)
      : BASE[m].avgWind,
  });

  // Last 3 months tabs
  const last3 = [3, 2, 1].map(offset => {
    let m = currentMonth - offset;
    let y = now.getFullYear();
    if (m <= 0) { m += 12; y -= 1; }
    return {
      key: `m${offset}`,
      label: `${monthNames[m - 1]} ${y}`,
      data: adjust(m),
    };
  });

  const avg3 = (months, key) => Math.round(months.reduce((s, m) => s + adjust(m)[key], 0) / months.length);

  const preMonsoonData = {
    avgTemp: avg3([3, 4, 5], 'avgTemp'),
    minTemp: adjust(3).minTemp,
    maxTemp: adjust(5).maxTemp,
    avgHumidity: avg3([3, 4, 5], 'avgHumidity'),
    totalRain: [3, 4, 5].reduce((s, m) => s + adjust(m).totalRain, 0),
    avgWind: avg3([3, 4, 5], 'avgWind'),
    condition: 'Hot & dry with thunderstorms',
  };

  const postMonsoonData = {
    avgTemp: avg3([10, 11], 'avgTemp'),
    minTemp: adjust(11).minTemp,
    maxTemp: adjust(10).maxTemp,
    avgHumidity: avg3([10, 11], 'avgHumidity'),
    totalRain: [10, 11].reduce((s, m) => s + adjust(m).totalRain, 0),
    avgWind: avg3([10, 11], 'avgWind'),
    condition: 'Pleasant & clear skies',
  };

  const tabs = [
    {
      key: 'preMonsoon', label: 'Pre-Monsoon', period: 'Mar - May',
      theme: { grad: 'linear-gradient(135deg,#fff6e0,#fde8b0)', accent: '#b8860b', icon: '☀️' },
      data: preMonsoonData,
    },
    {
      key: 'postMonsoon', label: 'Post-Monsoon', period: 'Oct - Nov',
      theme: { grad: 'linear-gradient(135deg,#f0f4ff,#dce8ff)', accent: '#4a6fa5', icon: '🌤️' },
      data: postMonsoonData,
    },
    ...last3.map(m => ({
      key: m.key, label: m.label, period: m.label,
      theme: { grad: 'linear-gradient(135deg,#f3f0ff,#e0d8ff)', accent: '#6a4fa5', icon: '🌡️' },
      data: m.data,
    })),
  ];

  const activeTab = tabs.find(t => t.key === active) || tabs[0];
  const w = activeTab.data;
  const theme = activeTab.theme;

  const StatRow = ({ icon, label, value, last }) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '9px 0', borderBottom: last ? 'none' : '1px solid #f0f7f3',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#5b7a6c', fontSize: 13 }}>
        {icon}{label}
      </div>
      <span style={{ fontWeight: 700, color: '#0b3d2e', fontSize: 13 }}>{value ?? '—'}</span>
    </div>
  );

  return (
    <div style={{
      background: 'linear-gradient(160deg, #e3f2fd 0%, #bbdefb 60%, #e8f5e9 140%)',
      borderRadius: 18,
      boxShadow: '0 8px 32px rgba(25,118,210,0.12)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      minHeight: 340,
    }}>
      {/* Decorative blobs */}
      <div style={{
        position: 'absolute', top: -40, right: -40,
        width: 160, height: 160, borderRadius: '50%',
        background: 'rgba(25,118,210,0.06)', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: -30, left: -30,
        width: 120, height: 120, borderRadius: '50%',
        background: 'rgba(67,160,71,0.05)', pointerEvents: 'none',
      }} />

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: 'rgba(25,118,210,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CloudRain size={16} color="#1976d2" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0d47a1', letterSpacing: 0.3 }}>
              Weather Details
            </div>
            {cityName && (
              <div style={{ fontSize: 11, color: '#1565c0', marginTop: 1 }}>
                📍 {cityName}
              </div>
            )}
          </div>
        </div>
        {/* Live badge */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: 'rgba(25,118,210,0.1)', borderRadius: 20,
          padding: '4px 10px',
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%', background: '#43a047',
            boxShadow: '0 0 6px #43a047',
            animation: 'livePulse 2s ease-in-out infinite',
          }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: '#1565c0', letterSpacing: 0.5 }}>
            LIVE
          </span>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{
        display: 'flex', overflowX: 'auto', gap: 6,
        padding: '12px 20px 0',
        scrollbarWidth: 'none',
      }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            style={{
              flex: '0 0 auto',
              padding: '6px 14px',
              border: active === t.key ? '1.5px solid rgba(25,118,210,0.5)' : '1.5px solid rgba(25,118,210,0.15)',
              borderRadius: 20,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 11,
              whiteSpace: 'nowrap',
              background: active === t.key ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.04)',
              color: active === t.key ? '#0d47a1' : '#90a4ae',
              transition: 'all 0.18s',
              backdropFilter: 'blur(4px)',
            }}
          >
            {t.theme.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Loading skeleton ── */}
      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8, animation: 'spin 2s linear infinite', display: 'inline-block' }}>🌀</div>
            <div style={{ color: '#5c8a9a', fontSize: 13 }}>Fetching weather…</div>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {!loading && error && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
            <div style={{ color: '#c62828', fontSize: 13, marginBottom: 12 }}>{error}</div>
            <button onClick={loadWeather} style={{
              background: 'rgba(25,118,210,0.1)', border: '1px solid rgba(25,118,210,0.3)',
              color: '#0d47a1', borderRadius: 8, padding: '6px 16px', fontSize: 12,
              fontWeight: 700, cursor: 'pointer',
            }}>Retry</button>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      {!loading && !error && (
        <>
          {/* Big temp hero */}
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            padding: '14px 20px 10px',
          }}>
            <div>
              <div style={{ fontSize: 56, fontWeight: 900, color: '#0d47a1', lineHeight: 1, letterSpacing: -2 }}>
                {w?.avgTemp != null ? w.avgTemp : '—'}
                <span style={{ fontSize: 24, fontWeight: 600, color: '#1565c0' }}>°C</span>
              </div>
              <div style={{ fontSize: 13, color: '#1565c0', marginTop: 4, fontWeight: 600 }}>
                {w?.condition || '—'}
              </div>
              {w?.minTemp != null && w?.maxTemp != null && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6,
                  background: 'rgba(25,118,210,0.1)', borderRadius: 20, padding: '3px 10px',
                }}>
                  <span style={{ fontSize: 11, color: '#1565c0', fontWeight: 700 }}>↓{w.minTemp}°</span>
                  <span style={{ fontSize: 10, color: '#90a4ae' }}>|</span>
                  <span style={{ fontSize: 11, color: '#c62828', fontWeight: 700 }}>↑{w.maxTemp}°</span>
                </div>
              )}
            </div>
            <div style={{ fontSize: 64, lineHeight: 1, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))' }}>
              {theme.icon}
            </div>
          </div>

          {/* Season label strip */}
          <div style={{
            margin: '0 20px 12px',
            background: 'rgba(25,118,210,0.08)',
            borderRadius: 8, padding: '5px 12px',
            display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
          }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#5c8a9a', letterSpacing: 1, textTransform: 'uppercase' }}>
              {activeTab.label}
            </span>
            <span style={{ fontSize: 10, color: '#5c8a9a' }}>·</span>
            <span style={{ fontSize: 10, color: '#5c8a9a', fontWeight: 600 }}>
              {activeTab.period}
            </span>
          </div>

          {/* Stat tiles grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 8, padding: '0 20px 18px',
          }}>
            {[
              {
                icon: '💧', label: 'Humidity',
                value: w?.avgHumidity != null ? `${w.avgHumidity}%` : '—',
                bar: w?.avgHumidity, barMax: 100, barColor: '#1976d2',
              },
              {
                icon: '🌧️', label: 'Rainfall',
                value: w?.totalRain != null ? `${w.totalRain} mm` : '—',
                bar: w?.totalRain, barMax: 300, barColor: '#43a047',
              },
              {
                icon: '💨', label: 'Wind Speed',
                value: w?.avgWind != null ? `${w.avgWind} km/h` : '—',
                bar: w?.avgWind, barMax: 40, barColor: '#0288d1',
              },
              {
                icon: '🌡️', label: 'Temp Range',
                value: w?.minTemp != null && w?.maxTemp != null
                  ? `${w.minTemp}°–${w.maxTemp}°C`
                  : w?.avgTemp != null ? `${w.avgTemp}°C` : '—',
                bar: w?.avgTemp, barMax: 50, barColor: '#e65100',
              },
            ].map(stat => (
              <div key={stat.label} style={{
                background: 'rgba(255,255,255,0.6)',
                borderRadius: 12, padding: '10px 12px',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(25,118,210,0.12)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 14 }}>{stat.icon}</span>
                  <span style={{ fontSize: 11, color: '#78909c', fontWeight: 600 }}>
                    {stat.label}
                  </span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#0d47a1', marginBottom: 6 }}>
                  {stat.value}
                </div>
                {/* Mini progress bar */}
                {stat.bar != null && (
                  <div style={{ height: 3, background: 'rgba(25,118,210,0.12)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(100, (stat.bar / stat.barMax) * 100)}%`,
                      background: stat.barColor,
                      borderRadius: 999,
                      transition: 'width 0.6s ease',
                    }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

/* ------------- Market — dynamic from Mandi API ------------- */
const Market = ({ location }) => {
  const [primaryCrop, setPrimaryCrop] = useState(null);
  const [otherCrops, setOtherCrops] = useState([]);
  const [resolvedPlace, setResolvedPlace] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMandiData = useCallback(async () => {
    if (!location) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(MANDI_API);
      const data = await res.json();
      const records = data.records || [];

      const loc = location.toLowerCase().trim();

      // city → home state mapping
      const cityToState = {
        'new delhi': 'Delhi', 'delhi': 'Delhi',
        'gurgaon': 'Haryana', 'gurugram': 'Haryana', 'faridabad': 'Haryana',
        'noida': 'Uttar Pradesh', 'ghaziabad': 'Uttar Pradesh',
        'mumbai': 'Maharashtra', 'pune': 'Maharashtra', 'nagpur': 'Maharashtra', 'nashik': 'Maharashtra',
        'bengaluru': 'Karnataka', 'bangalore': 'Karnataka', 'mysuru': 'Karnataka', 'hubli': 'Karnataka',
        'chennai': 'Tamil Nadu', 'coimbatore': 'Tamil Nadu', 'madurai': 'Tamil Nadu', 'salem': 'Tamil Nadu',
        'hyderabad': 'Telangana', 'warangal': 'Telangana', 'karimnagar': 'Telangana',
        'kolkata': 'West Bengal', 'howrah': 'West Bengal', 'durgapur': 'West Bengal',
        'jaipur': 'Rajasthan', 'jodhpur': 'Rajasthan', 'udaipur': 'Rajasthan', 'kota': 'Rajasthan',
        'lucknow': 'Uttar Pradesh', 'kanpur': 'Uttar Pradesh', 'agra': 'Uttar Pradesh', 'varanasi': 'Uttar Pradesh', 'allahabad': 'Uttar Pradesh', 'prayagraj': 'Uttar Pradesh',
        'patna': 'Bihar', 'gaya': 'Bihar', 'muzaffarpur': 'Bihar',
        'bhopal': 'Madhya Pradesh', 'indore': 'Madhya Pradesh', 'gwalior': 'Madhya Pradesh', 'jabalpur': 'Madhya Pradesh',
        'ahmedabad': 'Gujarat', 'surat': 'Gujarat', 'vadodara': 'Gujarat', 'rajkot': 'Gujarat',
        'chandigarh': 'Punjab', 'amritsar': 'Punjab', 'ludhiana': 'Punjab', 'jalandhar': 'Punjab',
        'dehradun': 'Uttarakhand', 'haridwar': 'Uttarakhand', 'rishikesh': 'Uttarakhand',
        'ranchi': 'Jharkhand', 'dhanbad': 'Jharkhand', 'jamshedpur': 'Jharkhand',
        'bhubaneswar': 'Odisha', 'cuttack': 'Odisha', 'rourkela': 'Odisha',
        'guwahati': 'Assam', 'dibrugarh': 'Assam', 'silchar': 'Assam',
        'shimla': 'Himachal Pradesh', 'manali': 'Himachal Pradesh', 'dharamsala': 'Himachal Pradesh',
        'thiruvananthapuram': 'Kerala', 'kochi': 'Kerala', 'kozhikode': 'Kerala', 'thrissur': 'Kerala',
        'raipur': 'Chhattisgarh', 'bilaspur': 'Chhattisgarh', 'durg': 'Chhattisgarh',
        'visakhapatnam': 'Andhra Pradesh', 'vijayawada': 'Andhra Pradesh', 'guntur': 'Andhra Pradesh',
        'srinagar': 'Jammu and Kashmir', 'jammu': 'Jammu and Kashmir',
        'imphal': 'Manipur', 'shillong': 'Meghalaya', 'aizawl': 'Mizoram',
        'kohima': 'Nagaland', 'agartala': 'Tripura', 'gangtok': 'Sikkim',
        'panaji': 'Goa',
      };

      // state → ordered nearby states (geographically closest first)
      const nearbyStates = {
        'Delhi': ['Haryana', 'Uttar Pradesh', 'Rajasthan', 'Punjab', 'Uttarakhand'],
        'Haryana': ['Delhi', 'Punjab', 'Uttar Pradesh', 'Rajasthan', 'Himachal Pradesh'],
        'Uttar Pradesh': ['Haryana', 'Delhi', 'Rajasthan', 'Madhya Pradesh', 'Bihar', 'Uttarakhand'],
        'Rajasthan': ['Haryana', 'Punjab', 'Uttar Pradesh', 'Madhya Pradesh', 'Gujarat'],
        'Punjab': ['Haryana', 'Himachal Pradesh', 'Rajasthan', 'Uttarakhand'],
        'Himachal Pradesh': ['Punjab', 'Haryana', 'Uttarakhand', 'Jammu and Kashmir'],
        'Uttarakhand': ['Uttar Pradesh', 'Himachal Pradesh', 'Haryana'],
        'Bihar': ['Uttar Pradesh', 'Jharkhand', 'West Bengal'],
        'Jharkhand': ['Bihar', 'West Bengal', 'Odisha', 'Chhattisgarh'],
        'West Bengal': ['Bihar', 'Jharkhand', 'Odisha', 'Assam'],
        'Odisha': ['West Bengal', 'Jharkhand', 'Chhattisgarh', 'Andhra Pradesh'],
        'Chhattisgarh': ['Madhya Pradesh', 'Odisha', 'Jharkhand', 'Maharashtra'],
        'Madhya Pradesh': ['Rajasthan', 'Uttar Pradesh', 'Chhattisgarh', 'Maharashtra', 'Gujarat'],
        'Maharashtra': ['Madhya Pradesh', 'Gujarat', 'Karnataka', 'Telangana', 'Goa'],
        'Gujarat': ['Rajasthan', 'Madhya Pradesh', 'Maharashtra'],
        'Karnataka': ['Maharashtra', 'Telangana', 'Andhra Pradesh', 'Tamil Nadu', 'Kerala', 'Goa'],
        'Telangana': ['Andhra Pradesh', 'Karnataka', 'Maharashtra', 'Chhattisgarh', 'Odisha'],
        'Andhra Pradesh': ['Telangana', 'Karnataka', 'Tamil Nadu', 'Odisha'],
        'Tamil Nadu': ['Karnataka', 'Andhra Pradesh', 'Kerala'],
        'Kerala': ['Tamil Nadu', 'Karnataka'],
        'Goa': ['Maharashtra', 'Karnataka'],
        'Assam': ['West Bengal', 'Meghalaya', 'Nagaland', 'Manipur', 'Tripura'],
        'Meghalaya': ['Assam', 'West Bengal'],
        'Nagaland': ['Assam', 'Manipur'],
        'Manipur': ['Nagaland', 'Assam', 'Mizoram'],
        'Mizoram': ['Manipur', 'Assam', 'Tripura'],
        'Tripura': ['Assam', 'Mizoram', 'West Bengal'],
        'Sikkim': ['West Bengal', 'Assam'],
        'Arunachal Pradesh': ['Assam', 'Nagaland'],
        'Jammu and Kashmir': ['Himachal Pradesh', 'Punjab'],
        'Ladakh': ['Jammu and Kashmir', 'Himachal Pradesh'],
      };

      // Step 1: exact district / market match
      let matched = records.filter(r =>
        r.district?.toLowerCase().includes(loc) ||
        r.market?.toLowerCase().includes(loc)
      );
      let place = location;

      // Step 2: home-state match
      if (matched.length === 0) {
        const homeState = cityToState[loc];
        if (homeState) {
          matched = records.filter(r => r.state?.toLowerCase() === homeState.toLowerCase());
          if (matched.length > 0) place = homeState;
        }
      }

      // Step 3: walk through geographically ordered neighbor states
      if (matched.length === 0) {
        const homeState = cityToState[loc] || loc;
        const neighbors = nearbyStates[homeState] ||
          nearbyStates[Object.keys(nearbyStates).find(k => k.toLowerCase() === homeState.toLowerCase())] ||
          ['Uttar Pradesh', 'Haryana', 'Rajasthan', 'Madhya Pradesh', 'Maharashtra'];

        for (const neighbor of neighbors) {
          const nb = records.filter(r => r.state?.toLowerCase() === neighbor.toLowerCase());
          if (nb.length > 0) {
            matched = nb;
            place = `${neighbor} (nearby)`;
            break;
          }
        }
      }

      // Step 4: absolute last resort — most-represented state in the dataset
      if (matched.length === 0) {
        const stateCounts = {};
        records.forEach(r => { if (r.state) stateCounts[r.state] = (stateCounts[r.state] || 0) + 1; });
        const topState = Object.entries(stateCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
        if (topState) {
          matched = records.filter(r => r.state === topState);
          place = topState;
        } else {
          matched = records.slice(0, 200);
          place = 'India';
        }
      }

      setResolvedPlace(place);

      // Group by commodity and compute average modal price
      const commodityMap = {};
      matched.forEach(r => {
        const name = r.commodity;
        const price = parseFloat(r.modal_price);
        if (!name || isNaN(price) || price <= 0) return;
        if (!commodityMap[name]) commodityMap[name] = { prices: [], markets: new Set() };
        commodityMap[name].prices.push(price);
        if (r.market) commodityMap[name].markets.add(r.market);
      });

      const sorted = Object.entries(commodityMap)
        .map(([name, d]) => ({
          name,
          avgPrice: Math.round(d.prices.reduce((s, p) => s + p, 0) / d.prices.length),
          minPrice: Math.round(Math.min(...d.prices)),
          maxPrice: Math.round(Math.max(...d.prices)),
          marketCount: d.markets.size,
          topMarket: [...d.markets][0] || place,
        }))
        .sort((a, b) => b.marketCount - a.marketCount || b.avgPrice - a.avgPrice);

      if (sorted.length === 0) throw new Error('No price data found');

      setPrimaryCrop(sorted[0]);
      setOtherCrops(sorted.slice(1, 5));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => { loadMandiData(); }, [loadMandiData]);

  const cropIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('wheat') || n.includes('gehun')) return '🌾';
    if (n.includes('rice') || n.includes('paddy') || n.includes('dhan')) return '🍚';
    if (n.includes('maize') || n.includes('corn') || n.includes('makka')) return '🌽';
    if (n.includes('onion') || n.includes('pyaz')) return '🧅';
    if (n.includes('potato') || n.includes('aloo')) return '🥔';
    if (n.includes('tomato')) return '🍅';
    if (n.includes('cotton') || n.includes('kapas')) return '🌿';
    if (n.includes('soybean') || n.includes('soya')) return '🫘';
    if (n.includes('mustard') || n.includes('sarson')) return '🌻';
    if (n.includes('sugarcane') || n.includes('ganna')) return '🎋';
    if (n.includes('banana') || n.includes('kela')) return '🍌';
    if (n.includes('mango') || n.includes('aam')) return '🥭';
    if (n.includes('chilli') || n.includes('mirch')) return '🌶️';
    if (n.includes('garlic') || n.includes('lahsun')) return '🧄';
    if (n.includes('gram') || n.includes('chana')) return '🫘';
    return '🌱';
  };

  return (
    <div style={{
      background: 'linear-gradient(160deg, #fffde7 0%, #fff8e1 60%, #ffecb3 100%)',
      borderRadius: 18,
      boxShadow: '0 8px 32px rgba(245,127,23,0.12)',
      overflow: 'hidden',
      position: 'relative',
      minHeight: 260,
    }}>
      {/* Blobs */}
      <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(245,127,23,0.05)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -30, left: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(245,127,23,0.05)', pointerEvents: 'none' }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(245,127,23,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
            🏪
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#e65100', letterSpacing: 0.3 }}>
              Market Prices
              {!loading && primaryCrop && <span style={{ fontWeight: 600, color: '#bf8a00' }}> – {primaryCrop.name}</span>}
            </div>
            <div style={{ fontSize: 11, color: '#bf8a00', marginTop: 1 }}>
              {loading ? 'Fetching live mandi prices…' : `📍 ${resolvedPlace}`}
            </div>
          </div>
        </div>
        {!loading && primaryCrop && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(245,127,23,0.1)', borderRadius: 20, padding: '4px 10px' }}>
            <TrendingUp size={11} color="#43a047" />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#e65100' }}>
              ₹{primaryCrop.minPrice}–{primaryCrop.maxPrice}
            </span>
          </div>
        )}
      </div>

      <div style={{ height: 1, background: 'rgba(245,127,23,0.12)', margin: '0 20px' }} />

      {/* Loading */}
      {loading && (
        <div style={{ padding: '32px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8, animation: 'spin 2s linear infinite', display: 'inline-block' }}>🔄</div>
          <div style={{ color: '#bf8a00', fontSize: 13 }}>Loading mandi prices…</div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div style={{ padding: '24px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
          <div style={{ color: '#bf8a00', fontSize: 13, marginBottom: 12 }}>{error}</div>
          <button onClick={loadMandiData} style={{ background: 'rgba(245,127,23,0.15)', border: '1px solid rgba(245,127,23,0.3)', color: '#e65100', borderRadius: 8, padding: '6px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Primary crop — big price hero */}
      {!loading && !error && primaryCrop && (
        <>
          <div style={{ padding: '14px 20px 10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 28 }}>{cropIcon(primaryCrop.name)}</span>
              <div>
                <div style={{ fontSize: 38, fontWeight: 900, color: '#e65100', lineHeight: 1, letterSpacing: -1 }}>
                  ₹{primaryCrop.avgPrice.toLocaleString()}
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#bf8a00' }}> /qtl</span>
                </div>
                <div style={{ fontSize: 12, color: '#8d6e00', marginTop: 3 }}>
                  📍 {primaryCrop.topMarket} · {primaryCrop.marketCount} market{primaryCrop.marketCount !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: 'rgba(245,127,23,0.12)', margin: '0 20px' }} />

          {/* Other crops */}
          {otherCrops.length > 0 && (
            <div style={{ padding: '10px 20px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#bf8a00', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
                Other Crops Nearby
              </div>
              {otherCrops.map((c, i) => (
                <div key={c.name} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '7px 0',
                  borderBottom: i < otherCrops.length - 1 ? '1px solid rgba(245,127,23,0.1)' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(245,127,23,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                      {cropIcon(c.name)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#5d4037' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#a1887f' }}>{c.marketCount} market{c.marketCount !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#e65100' }}>₹{c.avgPrice.toLocaleString()}</div>
                    <div style={{ fontSize: 10, color: '#a1887f' }}>avg/qtl</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tip */}
          <div style={{ margin: '0 20px 16px', background: 'rgba(245,127,23,0.08)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid rgba(245,127,23,0.2)' }}>
            <span style={{ fontSize: 16 }}>💡</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#bf8a00' }}>
              Prices from {resolvedPlace} mandis · ₹/Quintal · Data.gov.in
            </span>
          </div>
        </>
      )}
    </div>
  );
};

/* ------------- Schemes ------------- */
const Schemes = () => {
  const navigate = useNavigate();
  return (
    <div style={{
      background: 'linear-gradient(160deg, #e8f5e9 0%, #f1f8e9 50%, #dcedc8 100%)',
      borderRadius: 18,
      boxShadow: '0 8px 32px rgba(46,125,50,0.10)',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Blobs */}
      <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(46,125,50,0.05)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -30, left: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(46,125,50,0.05)', pointerEvents: 'none' }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(46,125,50,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
            🏛️
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1b5e20', letterSpacing: 0.3 }}>Schemes For You</div>
            <div style={{ fontSize: 11, color: '#558b2f', marginTop: 1 }}>Government benefits</div>
          </div>
        </div>
        <div style={{ background: 'rgba(46,125,50,0.1)', borderRadius: 20, padding: '4px 10px' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#2e7d32' }}>{SCHEMES.length} eligible</span>
        </div>
      </div>

      <div style={{ height: 1, background: 'rgba(46,125,50,0.1)', margin: '0 20px' }} />

      {/* Scheme rows */}
      <div style={{ padding: '10px 20px' }}>
        {SCHEMES.map((s, i) => (
          <div key={s.name} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 0',
            borderBottom: i < SCHEMES.length - 1 ? '1px solid rgba(46,125,50,0.08)' : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(46,125,50,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                {i === 0 ? '💰' : i === 1 ? '🛡️' : '🌱'}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#1b5e20', fontSize: 13 }}>{s.name}</div>
                <div style={{ color: '#558b2f', fontSize: 11, marginTop: 2 }}>{s.note}</div>
              </div>
            </div>
            <div style={{
              background: 'rgba(46,125,50,0.1)', border: '1px solid rgba(46,125,50,0.25)',
              borderRadius: 20, padding: '4px 10px',
              fontSize: 11, fontWeight: 700, color: '#2e7d32', flexShrink: 0, marginLeft: 8,
            }}>
              ✓ Eligible
            </div>
          </div>
        ))}
      </div>

      {/* CTA button */}
      <div style={{ padding: '4px 20px 16px' }}>
        <button
          onClick={() => navigate('/farmer/gov-schemes')}
          style={{
            width: '100%', padding: '11px 0',
            background: 'rgba(46,125,50,0.1)',
            border: '1.5px solid rgba(46,125,50,0.2)',
            borderRadius: 12, color: '#1b5e20', fontWeight: 700, fontSize: 13,
            cursor: 'pointer', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(46,125,50,0.18)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(46,125,50,0.1)'}
        >
          <Landmark size={14} color="#2e7d32" />
          View all schemes →
        </button>
      </div>
    </div>
  );
};

/* ------------- Main page ------------- */
const FarmerHome = () => {
  const [location, setLocation] = useState('New Delhi');

  // Detect user's city via the farmerApi service (geolocation + Nominatim)
  useEffect(() => {
    detectCity().then(city => setLocation(city));
  }, []);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const greetName = user?.role === 'farmer' && user?.name
    ? user.name.split(' ')[0]
    : 'Kisan';

  return (
    <>
      <Header />
      <div style={{ padding: '24px 0 0' }}>
        <Hero name={greetName} />

        <div style={{ marginBottom: 18 }}>
          <WeatherWidget location={location} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginTop: 18 }}>
          <ActiveCrops />
          <WeatherDetailCard location={location} />
        </div>

        <div style={{ ...sectionGap, ...grid(300) }}>
          <Market location={location} />
          <Schemes />
        </div>
      </div>

      <NotificationPopup />
      <Footer />
    </>
  );
};

export default FarmerHome;
